import {
  computed, onUnmounted, reactive, ref,
} from 'vue';
import { NestApiType, nestCall } from '@/composables/nestApi';
import {
  RcsAgent,
  RcsDedicatedFormState,
  RcsOnboardingStatus,
  RcsOnboardingStatusPayload,
  RcsWizardStepId,
  RCS_AGENT_PURPOSE_DEFAULT,
  RCS_BILLING_CATEGORY_DEFAULT,
  RCS_HOSTING_REGION_DEFAULT,
  applyCarrierHiddenDefaults,
  createEmptyCarrierRequirements,
  createEmptyPointOfContact,
  createEmptyRepresentative,
  createEmptyRcsForm,
  createEmptyTestNumbers,
  createEmptyVisualDesign,
  getRcsAgentCardState,
  getRcsReusableBrands,
  hydrateRepresentative,
  hydrateVisualDesign,
  isCarrierRequirementsComplete,
  isCarrierSelectionComplete,
  isRepresentativeComplete,
  isTestNumbersComplete,
  sanitizeTestNumbersForApi,
  isVisualDesignComplete,
  normalizeRcsPhoneNumber,
  normalizeRcsTestDevicePhoneNumber,
} from '@/types/rcs-onboarding';
import type {
  RcsBrand,
  RcsCarrierRequirements,
  RcsSelectedCarrier,
} from '@/types/rcs-onboarding';

const SENDER_ID_REGEX = /^[A-Za-z0-9_-]{2,50}$/;
const BASE_WIZARD_STEPS: RcsWizardStepId[] = [
  'basic_info',
  'carrier_selection',
  'visual',
  'representative',
  'carrier_requirements',
];

/** Stable client-side id for a brand-new agent draft. */
const generateLocalId = (): string => {
  const cryptoApi = (typeof crypto !== 'undefined' ? crypto : undefined) as
    | { randomUUID?: () => string }
    | undefined;
  if (cryptoApi && typeof cryptoApi.randomUUID === 'function') {
    return cryptoApi.randomUUID();
  }
  return `rcs_${Date.now()}_${Math.random().toString(36).slice(2)}`;
};

/**
 * First wizard step that still needs input for a given agent, so resuming a
 * draft jumps straight to the first incomplete step.
 */
function getResumeWizardStepIndex(agent: RcsAgent | null): number {
  const isOperational = agent?.onboarding_status === RcsOnboardingStatus.LAUNCHED;
  const basic = agent?.basic_info;
  const basicComplete = !!agent?.display_name?.trim()
    && SENDER_ID_REGEX.test(agent?.sender_id || '')
    && !!basic;
  if (!basicComplete) {
    return 0;
  }

  if (!isCarrierSelectionComplete(agent?.carrier_selection || { carriers: [] })) {
    return 1;
  }

  const visual = createEmptyVisualDesign();
  if (agent?.visual) {
    hydrateVisualDesign(visual, agent.visual);
  }
  if (!isVisualDesignComplete(visual)) {
    return 2;
  }

  const representative = createEmptyRepresentative();
  if (agent?.representative) {
    hydrateRepresentative(representative, agent.representative);
  }
  if (!isRepresentativeComplete(representative)) {
    return 3;
  }

  const carrier = agent?.carrier_requirements;
  if (!carrier || !isCarrierRequirementsComplete(carrier as RcsCarrierRequirements)) {
    return 4;
  }

  if (
    isOperational
    && !isTestNumbersComplete(agent?.test_numbers || createEmptyTestNumbers())
  ) {
    return 5;
  }

  return isOperational ? 5 : 4;
}

const hydrateCarrierRequirements = (
  target: RcsCarrierRequirements,
  source?: RcsCarrierRequirements,
): void => {
  if (!source) return;
  const empty = createEmptyCarrierRequirements();
  target.points_of_contact = source.points_of_contact?.length
    ? source.points_of_contact.map((contact) => ({
      first_name: contact.first_name || '',
      last_name: contact.last_name || '',
      job_title: contact.job_title || '',
      email: contact.email || '',
      brand_website: contact.brand_website || '',
    }))
    : [createEmptyPointOfContact()];
  target.traffic_estimates = {
    average_global_traffic:
      source.traffic_estimates?.average_global_traffic
      || empty.traffic_estimates.average_global_traffic,
    average_message_rate_per_user:
      source.traffic_estimates?.average_message_rate_per_user
      || empty.traffic_estimates.average_message_rate_per_user,
    users_targeted:
      source.traffic_estimates?.users_targeted
      || empty.traffic_estimates.users_targeted,
  };
  applyCarrierHiddenDefaults(target);
};

/**
 * Centralised state machine for the multi-agent RCS onboarding.
 *
 * A shop owns a single brand and any number of dedicated agents (drafts
 * included). The composable exposes the agents list and per-agent wizard
 * actions; components decide which screen to mount.
 */
export function useRcsOnboarding(shopId: number) {
  const status = ref<RcsOnboardingStatusPayload | null>(null);
  const isLoading = ref(false);
  const isSubmitting = ref(false);
  const lastError = ref<string | null>(null);

  /** Vonage carrier reference list (US excluded), loaded lazily for the wizard. */
  const availableCarriers = ref<RcsSelectedCarrier[]>([]);
  const carriersLoading = ref(false);
  const carriersError = ref(false);

  const form = reactive<RcsDedicatedFormState>(createEmptyRcsForm());
  const currentWizardStepIndex = ref(0);
  /** Agent currently being created/edited in the wizard. */
  const currentAgentLocalId = ref<string | null>(null);

  let pollHandle: ReturnType<typeof setInterval> | null = null;

  const agents = computed<RcsAgent[]>(() => status.value?.agents || []);
  const currentAgent = computed<RcsAgent | null>(
    () => agents.value.find((a) => a.local_id === currentAgentLocalId.value) || null,
  );
  const currentAgentIsDirty = computed(
    () => !!currentAgent.value?.is_dirty,
  );
  /** Already-created brands (with a Vonage id) reusable in the basic-info step. */
  const reusableBrands = computed<RcsBrand[]>(() => getRcsReusableBrands(agents.value));
  const canManageTestNumbers = computed(
    () => currentAgent.value?.onboarding_status === RcsOnboardingStatus.LAUNCHED,
  );
  const wizardSteps = computed<RcsWizardStepId[]>(() => (
    canManageTestNumbers.value
      ? [...BASE_WIZARD_STEPS, 'test_numbers']
      : BASE_WIZARD_STEPS
  ));

  const currentWizardStep = computed<RcsWizardStepId>(
    () => wizardSteps.value[currentWizardStepIndex.value] || wizardSteps.value[0],
  );
  const isFirstWizardStep = computed(() => currentWizardStepIndex.value === 0);
  const isLastWizardStep = computed(
    () => currentWizardStepIndex.value === wizardSteps.value.length - 1,
  );

  // Poll while any agent is still being validated by Vonage.
  const isPollingNeeded = computed(() => agents.value.some((a) => [
    RcsOnboardingStatus.AGENT_LINKED,
    RcsOnboardingStatus.PENDING_APPROVAL,
    RcsOnboardingStatus.AGENT_DRAFTED,
  ].includes(a.onboarding_status)));

  const stopPolling = () => {
    if (pollHandle) {
      clearInterval(pollHandle);
      pollHandle = null;
    }
  };

  const refresh = async (): Promise<void> => {
    try {
      const response = await nestCall(
        'get',
        NestApiType.MESSAGING,
        '/rcs/onboarding/status',
        {},
        '',
        undefined,
        true,
        { shop_id: Number(shopId) },
      );
      if (response?.data?.data) {
        status.value = response.data.data as RcsOnboardingStatusPayload;
      } else if (response?.data) {
        status.value = response.data as RcsOnboardingStatusPayload;
      }
      lastError.value = null;
    } catch (err: any) {
      lastError.value = err?.response?.data?.code || 'rcs_status_fetch_failed';
    }
  };

  const startPolling = () => {
    if (pollHandle) return;
    pollHandle = setInterval(async () => {
      await refresh();
      if (!isPollingNeeded.value) {
        stopPolling();
      }
    }, 30000);
  };

  const init = async (): Promise<void> => {
    isLoading.value = true;
    try {
      await refresh();
      if (isPollingNeeded.value) {
        startPolling();
      }
    } finally {
      isLoading.value = false;
    }
  };

  const activate = async (): Promise<void> => {
    isSubmitting.value = true;
    try {
      const response = await nestCall(
        'post',
        NestApiType.MESSAGING,
        '/rcs/onboarding/activate',
        {},
        '',
        { shop_id: Number(shopId) },
      );
      if (response?.data?.data) {
        status.value = response.data.data as RcsOnboardingStatusPayload;
      }
      await refresh();
      lastError.value = null;
    } catch (err: any) {
      lastError.value = err?.response?.data?.code || 'rcs_activate_failed';
      throw err;
    } finally {
      isSubmitting.value = false;
    }
  };

  /**
   * Load the wizard form with an agent's persisted payload (or empty defaults
   * for a brand-new draft). Brand display name comes from the shop brand.
   */
  const hydrateFormFromAgent = (agent: RcsAgent | null): void => {
    Object.assign(form, createEmptyRcsForm());
    // Brand now lives on the agent (a fresh draft starts with an empty brand).
    form.basic_info.brand_id = agent?.brand?.id || '';
    form.basic_info.brand_display_name = agent?.brand?.display_name || '';
    if (!agent) return;
    form.basic_info.agent_display_name = agent.display_name || '';
    form.basic_info.sender_id = agent.sender_id || '';
    form.basic_info.agent_purpose =
      agent.basic_info?.agent_purpose || RCS_AGENT_PURPOSE_DEFAULT;
    form.basic_info.hosting_region =
      agent.basic_info?.hosting_region || RCS_HOSTING_REGION_DEFAULT;
    form.basic_info.billing_category =
      agent.basic_info?.billing_category || RCS_BILLING_CATEGORY_DEFAULT;
    if (agent.carrier_selection?.carriers?.length) {
      form.carrier_selection.carriers = agent.carrier_selection.carriers.map(
        (carrier) => ({ ...carrier }),
      );
    }
    if (agent.test_numbers?.numbers?.length) {
      form.test_numbers.numbers = agent.test_numbers.numbers.map((entry) => ({
        number: entry.number || '',
      }));
    }
    if (agent.carrier_requirements) {
      hydrateCarrierRequirements(
        form.carrier_requirements,
        agent.carrier_requirements as RcsCarrierRequirements,
      );
    }
    if (agent.visual) {
      hydrateVisualDesign(form.visual, agent.visual);
    }
    if (agent.representative) {
      hydrateRepresentative(form.representative, agent.representative);
    }
  };

  /** Open the wizard to create a brand-new agent. */
  const startCreateAgent = (): void => {
    currentAgentLocalId.value = generateLocalId();
    hydrateFormFromAgent(null);
    currentWizardStepIndex.value = 0;
  };

  /** Open the wizard to edit an existing agent (resumes at first gap). */
  const startEditAgent = (localId: string): void => {
    currentAgentLocalId.value = localId;
    const agent = agents.value.find((a) => a.local_id === localId) || null;
    hydrateFormFromAgent(agent);
    const maxIndex = wizardSteps.value.length - 1;
    const resolved = getResumeWizardStepIndex(agent);
    currentWizardStepIndex.value = Math.min(Math.max(resolved, 0), maxIndex);
  };

  const goNextWizardStep = (): void => {
    if (!isLastWizardStep.value) {
      currentWizardStepIndex.value += 1;
    }
  };

  const goPreviousWizardStep = (): void => {
    if (!isFirstWizardStep.value) {
      currentWizardStepIndex.value -= 1;
    }
  };

  const goToWizardStep = (index: number): void => {
    if (index < 0 || index >= wizardSteps.value.length) return;
    currentWizardStepIndex.value = index;
  };

  /** Throw a normalized error from a draft-save nestCall response. */
  const assertDraftResponse = (response: any): void => {
    if (response?.error) {
      throw { response: { data: response.error } };
    }
    if (!response?.success) {
      throw new Error('rcs_save_failed');
    }
  };

  const fetchAvailableCarriers = async (): Promise<void> => {
    if (availableCarriers.value.length && !carriersError.value) {
      return;
    }
    carriersLoading.value = true;
    carriersError.value = false;
    try {
      const response = await nestCall(
        'get',
        NestApiType.MESSAGING,
        '/rcs/onboarding/carriers',
        {},
        '',
        undefined,
        true,
      );
      const carriers = response?.data?.data?.carriers
        || response?.data?.carriers
        || [];
      availableCarriers.value = carriers as RcsSelectedCarrier[];
    } catch (err: any) {
      carriersError.value = true;
      lastError.value = err?.response?.data?.code || 'rcs_carriers_fetch_failed';
    } finally {
      carriersLoading.value = false;
    }
  };

  const saveCarrierSelectionDraft = async (): Promise<void> => {
    isSubmitting.value = true;
    try {
      const response = await nestCall(
        'post',
        NestApiType.MESSAGING,
        '/rcs/onboarding/save-carrier-selection',
        {},
        '',
        {
          shop_id: Number(shopId),
          agent_local_id: currentAgentLocalId.value,
          carrier_selection: form.carrier_selection,
        },
      );
      assertDraftResponse(response);
      lastError.value = null;
      void refresh();
    } catch (err: any) {
      lastError.value = err?.response?.data?.code || 'rcs_save_carrier_selection_failed';
      throw err;
    } finally {
      isSubmitting.value = false;
    }
  };

  const saveBasicInfoDraft = async (): Promise<void> => {
    isSubmitting.value = true;
    try {
      const response = await nestCall(
        'post',
        NestApiType.MESSAGING,
        '/rcs/onboarding/save-basic-info',
        {},
        '',
        {
          shop_id: Number(shopId),
          agent_local_id: currentAgentLocalId.value,
          basic_info: form.basic_info,
        },
      );
      assertDraftResponse(response);
      lastError.value = null;
      void refresh();
    } catch (err: any) {
      lastError.value = err?.response?.data?.code || 'rcs_save_basic_info_failed';
      throw err;
    } finally {
      isSubmitting.value = false;
    }
  };

  const saveCarrierRequirementsDraft = async (): Promise<void> => {
    isSubmitting.value = true;
    try {
      applyCarrierHiddenDefaults(form.carrier_requirements);
      const response = await nestCall(
        'post',
        NestApiType.MESSAGING,
        '/rcs/onboarding/save-carrier-requirements',
        {},
        '',
        {
          shop_id: Number(shopId),
          agent_local_id: currentAgentLocalId.value,
          carrier_requirements: form.carrier_requirements,
        },
      );
      assertDraftResponse(response);
      lastError.value = null;
      void refresh();
    } catch (err: any) {
      lastError.value = err?.response?.data?.code || 'rcs_save_carrier_requirements_failed';
      throw err;
    } finally {
      isSubmitting.value = false;
    }
  };

  const saveTestNumbersDraft = async (): Promise<void> => {
    isSubmitting.value = true;
    try {
      form.test_numbers = sanitizeTestNumbersForApi(form.test_numbers);
      const response = await nestCall(
        'post',
        NestApiType.MESSAGING,
        '/rcs/onboarding/save-test-numbers',
        {},
        '',
        {
          shop_id: Number(shopId),
          agent_local_id: currentAgentLocalId.value,
          test_numbers: form.test_numbers,
        },
      );
      assertDraftResponse(response);
      lastError.value = null;
      void refresh();
    } catch (err: any) {
      lastError.value = err?.response?.data?.code || 'rcs_save_test_numbers_failed';
      throw err;
    } finally {
      isSubmitting.value = false;
    }
  };

  const saveRepresentativeDraft = async (): Promise<void> => {
    isSubmitting.value = true;
    try {
      const response = await nestCall(
        'post',
        NestApiType.MESSAGING,
        '/rcs/onboarding/save-representative',
        {},
        '',
        {
          shop_id: Number(shopId),
          agent_local_id: currentAgentLocalId.value,
          representative: form.representative,
        },
      );
      assertDraftResponse(response);
      lastError.value = null;
      void refresh();
    } catch (err: any) {
      lastError.value = err?.response?.data?.code || 'rcs_save_representative_failed';
      throw err;
    } finally {
      isSubmitting.value = false;
    }
  };

  const saveVisualDesignDraft = async (): Promise<void> => {
    isSubmitting.value = true;
    try {
      form.visual.phone.number = normalizeRcsPhoneNumber(form.visual.phone.number);
      const response = await nestCall(
        'post',
        NestApiType.MESSAGING,
        '/rcs/onboarding/save-visual-design',
        {},
        '',
        {
          shop_id: Number(shopId),
          agent_local_id: currentAgentLocalId.value,
          visual: form.visual,
        },
      );
      assertDraftResponse(response);
      lastError.value = null;
      void refresh();
    } catch (err: any) {
      lastError.value = err?.response?.data?.code || 'rcs_save_visual_design_failed';
      throw err;
    } finally {
      isSubmitting.value = false;
    }
  };

  const pushAgentChanges = async (localId: string): Promise<void> => {
    isSubmitting.value = true;
    try {
      await nestCall(
        'post',
        NestApiType.MESSAGING,
        '/rcs/onboarding/push-agent',
        {},
        '',
        { shop_id: Number(shopId), agent_local_id: localId },
      );
      await refresh();
      if (isPollingNeeded.value) {
        startPolling();
      }
      lastError.value = null;
    } catch (err: any) {
      lastError.value = err?.response?.data?.code
        || err?.response?.data?.message
        || 'rcs_push_agent_failed';
      throw err;
    } finally {
      isSubmitting.value = false;
    }
  };

  const submitDedicated = async (): Promise<void> => {
    isSubmitting.value = true;
    try {
      applyCarrierHiddenDefaults(form.carrier_requirements);
      form.visual.phone.number = normalizeRcsPhoneNumber(form.visual.phone.number);
      await nestCall(
        'post',
        NestApiType.MESSAGING,
        '/rcs/onboarding/connect-dedicated',
        {},
        '',
        {
          shop_id: Number(shopId),
          agent_local_id: currentAgentLocalId.value,
          basic_info: form.basic_info,
          carrier_selection: form.carrier_selection,
          test_numbers: sanitizeTestNumbersForApi(form.test_numbers),
          carrier_requirements: form.carrier_requirements,
          visual: form.visual,
          representative: form.representative,
        },
      );
      await refresh();
      if (isPollingNeeded.value) {
        startPolling();
      }
      lastError.value = null;
    } catch (err: any) {
      lastError.value = err?.response?.data?.code
        || err?.response?.data?.message
        || 'rcs_connect_failed';
      throw err;
    } finally {
      isSubmitting.value = false;
    }
  };

  const deleteAgent = async (localId: string): Promise<void> => {
    isSubmitting.value = true;
    try {
      await nestCall(
        'delete',
        NestApiType.MESSAGING,
        '/rcs/onboarding/agents',
        {},
        '',
        {
          shop_id: Number(shopId),
          agent_local_id: localId,
        },
      );
      await refresh();
      lastError.value = null;
    } catch (err: any) {
      lastError.value = err?.response?.data?.code || 'rcs_delete_agent_failed';
      throw err;
    } finally {
      isSubmitting.value = false;
    }
  };

  const retry = async (localId: string): Promise<void> => {
    isSubmitting.value = true;
    try {
      await nestCall(
        'post',
        NestApiType.MESSAGING,
        '/rcs/onboarding/retry',
        {},
        '',
        { shop_id: Number(shopId), agent_local_id: localId },
      );
      await refresh();
      lastError.value = null;
    } catch (err: any) {
      lastError.value = err?.response?.data?.code || 'rcs_retry_failed';
      throw err;
    } finally {
      isSubmitting.value = false;
    }
  };

  onUnmounted(stopPolling);

  return {
    // state
    status,
    form,
    agents,
    currentAgent,
    currentAgentIsDirty,
    reusableBrands,
    currentAgentLocalId,
    isLoading,
    isSubmitting,
    lastError,
    availableCarriers,
    carriersLoading,
    carriersError,
    currentWizardStep,
    wizardSteps,
    currentWizardStepIndex,
    isFirstWizardStep,
    isLastWizardStep,
    isPollingNeeded,
    // helpers
    getRcsAgentCardState,
    // actions
    init,
    refresh,
    activate,
    startCreateAgent,
    startEditAgent,
    goNextWizardStep,
    goPreviousWizardStep,
    goToWizardStep,
    fetchAvailableCarriers,
    saveBasicInfoDraft,
    saveCarrierSelectionDraft,
    saveTestNumbersDraft,
    saveCarrierRequirementsDraft,
    saveVisualDesignDraft,
    saveRepresentativeDraft,
    pushAgentChanges,
    submitDedicated,
    deleteAgent,
    retry,
    stopPolling,
  };
}

export type UseRcsOnboarding = ReturnType<typeof useRcsOnboarding>;
